import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, ChevronRight, X, Menu, User, Settings, CreditCard, BellRing, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useMyMenuPermissions } from '../hooks/useMenuPermissions';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import NotificationsBell from './NotificationsBell';

function deriveBreadcrumb(pathname: string): string[] {
  const parts = pathname.replace('/app/', '').split('/').filter(Boolean);
  if (parts.length === 0) return ['Dashboard'];
  return parts.map((p) => p.replace(/-/g, ' '));
}

interface TopBarProps {
  onMenuToggle: () => void;
}

export default function TopBar({ onMenuToggle }: TopBarProps) {
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const { titles } = useBreadcrumb();
  const rawCrumbs = deriveBreadcrumb(location.pathname);
  // Raw URL parts (preserve hyphens) used as keys for registered titles
  const urlParts = location.pathname.replace('/app/', '').split('/').filter(Boolean);
  // Replace raw URL segments (e.g. UUIDs) with registered human-readable titles
  const crumbs = rawCrumbs.map((c, i) => titles[urlParts[i]] ?? c);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join('').toUpperCase() || '?';
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { data: myPermsData } = useMyMenuPermissions();
  const isOwner = user?.orgRole === 'owner';
  const canSeeBilling = isOwner || (myPermsData?.data?.allowed_menus ?? []).includes('settings_billing');

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  function navigate_and_close(path: string) {
    setMenuOpen(false);
    navigate(path);
  }

  return (
    <div className="tb-bar">
      <button className="tb-menu-btn" onClick={onMenuToggle} aria-label="Toggle menu">
        <Menu size={22} />
      </button>
      <div className="tb-breadcrumb">
        <span className="tb-breadcrumb-root">App</span>
        {crumbs.map((crumb, i) => (
          <span key={i} className="tb-breadcrumb-item">
            <ChevronRight size={14} />
            <span className={i === crumbs.length - 1 ? 'tb-breadcrumb-current' : ''}>
              {crumb}
            </span>
          </span>
        ))}
      </div>

      <div className="tb-right">
        {searchOpen ? (
          <div className="tb-search-wrap">
            <Search size={16} className="tb-search-icon" />
            <input
              className="tb-search-input"
              placeholder="Search..."
              autoFocus
              onBlur={() => setSearchOpen(false)}
            />
            <button className="tb-icon-btn d-none" onClick={() => setSearchOpen(false)} style={{ marginLeft: 4 }}>
              <X size={16} />
            </button>
          </div>
        ) : (
          <button className="tb-icon-btn d-none" onClick={() => setSearchOpen(true)}>
            <Search size={18} />
          </button>
        )}

        <NotificationsBell />

        <div className="tb-avatar-wrap" ref={menuRef}>
          <div className="tb-avatar" onClick={() => setMenuOpen((o) => !o)}>
            {user?.avatarUrl
              ? <img src={user.avatarUrl} alt={user.fullName || initials} className="tb-avatar-img" />
              : initials}
          </div>
          {menuOpen && (
            <div className="tb-account-menu">
              <div className="tb-account-menu-header">
                <div className="tb-account-menu-name">{user?.fullName || user?.email}</div>
                <div className="tb-account-menu-email">{user?.email}</div>
              </div>
              <div className="tb-account-menu-divider" />
              <button className="tb-account-menu-item" onClick={() => navigate_and_close('/app/settings')}>
                <User size={15} /> My Account
              </button>
              <button className="tb-account-menu-item" onClick={() => navigate_and_close('/app/settings?tab=Account')}>
                <Settings size={15} /> Settings
              </button>
              {canSeeBilling && (
                <button className="tb-account-menu-item" onClick={() => navigate_and_close('/app/settings?tab=Billing')}>
                  <CreditCard size={15} /> Billing
                </button>
              )}
              <button className="tb-account-menu-item" onClick={() => navigate_and_close('/app/settings?tab=Notifications')}>
                <BellRing size={15} /> Notifications
              </button>
              <div className="tb-account-menu-divider" />
              <button className="tb-account-menu-item tb-account-menu-logout" onClick={() => { setMenuOpen(false); logout(); }}>
                <LogOut size={15} /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
      <style>{`
        .tb-bar {
          height: 56px;
          background: var(--white, #fff);
          border-bottom: 1px solid var(--gray-200);
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 24px;
          position: sticky;
          top: 0;
          z-index: 50;
        }
        .tb-menu-btn {
          display: none;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          color: var(--gray-900);
          margin-right: 8px;
        }
        .tb-breadcrumb {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          color: var(--gray-500);
          min-width: 0;
          overflow: hidden;
        }
        .tb-breadcrumb-root { color: var(--gray-500); flex-shrink: 0; }
        .tb-breadcrumb-item { display: flex; align-items: center; gap: 6px; min-width: 0; }
        .tb-breadcrumb-current {
          color: var(--gray-900); font-weight: 600;
          text-transform: capitalize; white-space: nowrap;
          overflow: hidden; text-overflow: ellipsis;
        }
        .tb-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
        .tb-icon-btn {
          width: 36px; height: 36px; border-radius: 8px; border: none;
          background: transparent; display: flex; align-items: center;
          justify-content: center; cursor: pointer; color: var(--gray-500);
          transition: all 0.15s; position: relative; flex-shrink: 0;
        }
.tb-search-wrap { position: relative; display: flex; align-items: center; }
        .tb-search-icon { position: absolute; left: 10px; color: var(--gray-500); pointer-events: none; }
        .tb-search-input {
          height: 36px; border: 1px solid var(--gray-200); border-radius: 8px;
          padding: 0 12px 0 36px; font-size: 14px; outline: none;
          width: 200px; transition: width 0.2s; background: var(--gray-50);
        }
        .tb-avatar-wrap { position: relative; margin-left: 4px; flex-shrink: 0; }
        .tb-avatar {
          width: 32px; height: 32px; border-radius: 50%;
          background: var(--sky); display: flex; align-items: center;
          justify-content: center; color: #fff; font-size: 12px;
          font-weight: 600; cursor: pointer; overflow: hidden;
        }
        .tb-avatar-img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
        .tb-account-menu {
          position: absolute; top: calc(100% + 8px); right: 0;
          background: #fff; border: 1px solid var(--gray-200);
          border-radius: 10px; box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          min-width: 200px; z-index: 200; overflow: hidden;
        }
        .tb-account-menu-header { padding: 12px 14px 10px; }
        .tb-account-menu-name { font-size: 13px; font-weight: 600; color: var(--gray-900); }
        .tb-account-menu-email { font-size: 12px; color: var(--gray-500); margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .tb-account-menu-divider { height: 1px; background: var(--gray-200); margin: 2px 0; }
        .tb-account-menu-item {
          display: flex; align-items: center; gap: 9px;
          width: 100%; padding: 9px 14px; border: none;
          background: transparent; font-size: 13px; color: var(--gray-700);
          cursor: pointer; text-align: left; transition: background 0.12s;
        }
        .tb-account-menu-item:hover { background: var(--gray-50); }
        .tb-account-menu-logout { color: var(--red); }
        .tb-account-menu-logout:hover { background: #fff0f0; }
        @media (max-width: 768px) {
          .tb-bar { padding: 0 12px; height: 52px; }
          .tb-menu-btn { display: flex; }
          .tb-breadcrumb { font-size: 13px; }
          .tb-search-input { width: 140px; }
        }
        @media (max-width: 480px) {
          .tb-bar { padding: 0 8px; height: 48px; }
          .tb-breadcrumb { font-size: 12px; gap: 4px; }
          .tb-search-input { width: 120px; font-size: 13px; }
          .tb-avatar { width: 28px; height: 28px; font-size: 10px; }
          .tb-avatar-wrap { margin-left: 0; }
          .tb-right { gap: 4px; }
        }
      `}</style>
    </div>
  );
}
